import { PaymentService } from 'src/app/angularJS/services/payments/payments.service';
import { JourneyService } from 'src/app/angularJS/services/journey/journey.service';
import { VariantService } from 'src/app/angularJS/services/variant/variant.service';

/**
 * A Controller using the {@link actora.services.PaymentService} to handle the retrieval and display of ticket and journey details.
 */
export class TicketHistoryController {

  public static $inject = ['$stateParams', '$rootScope', '$state', 'paymentService', 'paymentConstants', 'journeyService', 'variantService', '$q'];
  public paginationData: InterACT.Interfaces.IPaginationData;
  public productCatalogueList: Array<InterACT.Interfaces.ICatalogueProduct> = [];
  public journeyHistoryList: Array<InterACT.Interfaces.Journeys.Journey>;
  public ticketStatus = {};
  public zeroProductHistory: boolean;
  public totalCost: number;
  public purchasedDate: string;
  public expandableRowIndex: undefined | number;
  private paymentHistoryId: string;
  constructor(
    private $stateParams,
    private $rootScope,
    private $state: ng.ui.IStateService,
    private paymentService: PaymentService,
    private paymentConstants,
    private journeyService: JourneyService,
    private variantService: VariantService,
    private $q: ng.IQService
  ) {
    this.paymentHistoryId = this.$stateParams.paymentHistoryId;
    this.ticketStatus = this.paymentConstants.PAYMENT_STATUS;
    this.paginationData = { perPageCount: 15, totalCount: 0 };
    this.zeroProductHistory = false;
  }

  /**
   * @description A method which is used to toggle journey details
   *
   * @param index The row index
   * @param journeys for product link
   */
  public togglePanel(index: number, journeys?: Array<InterACT.Interfaces.UsagePatterns.JourneyLink>) {
    if (this.expandableRowIndex !== index) {
      this.expandableRowIndex = index;
      const journeysLink: Array<string> = [];
      journeys.forEach((journey) => {
        journeysLink.push(journey.link.split('/').pop());
      });
      const journeysLinks = journeysLink.join();
      this.getJourneyHistoryList(journeysLinks);
    } else {
      this.expandableRowIndex = undefined;
    }
  }
  /**
   * A method which is used to get ticket history details for a particular payment.
   *
   * @param page The number of page of data to be retrieved
   * @param perPage The number of items per page to be retrieved
   */
  public fetchTicketHistory(page?: number, perPage?: number) {
    const deferred = this.$q.defer();
    this.$rootScope.showSpinner = true;

    if (_.isEmpty(this.paymentHistoryId)) {
      this.zeroProductHistory = true;
      this.$rootScope.showSpinner = false;
      deferred.resolve();
      return deferred.promise;
    } else {
      this.setPageData(page, perPage);
      this.zeroProductHistory = false;
      return this.getProductDetails(page, perPage);
    }
  }

  /**
   * A method which is used to get single payment history based on payment id.
   *
   * @param page the new current page
   * @param perPage the new per page count
   */
  private getProductDetails = (page?: number, perPage?: number) => {
    this.setPageData(page, perPage);
    return this.paymentService.getPaymentRequest(this.paymentHistoryId).then((paymentRequest: InterACT.Interfaces.Payments.PaymentRequest) => {
      this.totalCost = paymentRequest.amount;
      this.purchasedDate = paymentRequest.date;
      const usagePatternLink: Array<string> = [];
      paymentRequest.items.forEach((link) => {
        usagePatternLink.push(link.split('/').pop());
      });

      this.getUsagePatternByID(usagePatternLink, page, perPage);
    });
  }
  /**
   * A method which is used to get catalogue and prepare the final product history for a particular payment.
   *
   * @param usagePatternLink list of usage pattern link
   * @param page the new current page
   * @param perPage the new per page count
   */
  private getUsagePatternByID = (usagePatternLink?: Array<string>, page?: number, perPage?: number) => {
    this.setPageData(page, perPage);

    if (_.isUndefined(usagePatternLink)) {
      this.zeroProductHistory = true;
      this.$rootScope.showSpinner = false;
    } else {
      const usagePatternLinks = usagePatternLink.join();
      const qParam = 'id in (' + usagePatternLinks + ')';

      return this.paymentService.getUsagePattern(this.paginationData.currentPage,
        this.paginationData.perPageCount, qParam).then((usagePatternsList: InterACT.Interfaces.UsagePatterns.UsagePatternsResponse) => {
          this.paginationData.totalCount = usagePatternsList.meta.totalCount;
          this.getProductCatalogueList(usagePatternsList.usagePatterns);
        });
    }
  }

  /**
   * A method which is used to get catalogue and prepare the final product history for a particular payment.
   *
   * @param usagePatternsList list of product
   * @param page the new current page
   * @param perPage the new per page count
   */
  private getProductCatalogueList(usagePatternsList?: Array<InterACT.Interfaces.UsagePatterns.UsagePattern>, page?: number, perPage?: number) {
    this.setPageData(page, perPage);
    usagePatternsList.forEach((usagePatterns) => {
      usagePatterns.products.forEach((product) => {
        const qParam = product.product.catalogueItem.link;
        const catalogueId = qParam.split('/')[2];
        const productId = qParam.split('/')[4];
        const variantId = qParam.split('/').pop();
        if ((catalogueId) && (productId) && (variantId)) {
          this.variantService.getVariant(catalogueId, parseInt(productId), parseInt(variantId)).then((variants) => {
            this.addProductCatalogue(usagePatterns.id, product, undefined, variants.price);
            this.$rootScope.showSpinner = false;
          });
        } else {
          this.addProductCatalogue(usagePatterns.id, product, undefined, 0);
          this.$rootScope.showSpinner = false;
        }
      });
    });
  }
  /**
   * A method which add each product into productCatalogueList.
   *
   * @param id the usage pattern product id
   * @param product for a payment
   * @param status of a product
   * @param price of a product
   */
  private addProductCatalogue = (id: string, product: InterACT.Interfaces.UsagePatterns.ProductJourneyMapping, status: string, price: number) => {
    this.productCatalogueList.push({
      id: id,
      products: product,
      status: _.isUndefined(status) ? '' : status,
      amount: price
    });
    this.$rootScope.showSpinner = false;
  }
  /**
   * This function is used to set the paginator data for requests
   *
   * @param page the new current page
   * @param perPage the new per page count
   */
  private setPageData(page?: number, perPage?: number) {
    this.paginationData.currentPage = angular.isNumber(page) ? page : 1;
    this.paginationData.perPageCount = angular.isNumber(perPage) ? perPage : this.paginationData.perPageCount;
  }
  /**
   * A method which changes the state to PaymentHistory page
   */
  private navigateToPaymentHistory = () => {
    this.$state.go('customer.payments.list');
  }

  /**
   * A method which is used to get history of journeys done on a selected purchased ticket.
   *
   * @param journeyIds Journey ids of journey API
   */
  private getJourneyHistoryList(journeysLinks: string) {
    this.$rootScope.showSpinner = true;
    this.journeyHistoryList = [];
    const qParam = 'id in (' + journeysLinks + ')';
    this.journeyService.getList(undefined, undefined, qParam)
      .then((result: InterACT.Interfaces.Journeys.JourneysResponse) => {
        this.journeyHistoryList = result.journeys;
        this.$rootScope.showSpinner = false;
      });
  }
}
